import { useEffect, useRef, useState } from "react";
import Cropper, { type Area, type Point } from "react-easy-crop";
import { createCroppedAvatarBlob } from "./avatarCrop";
import "./profile-photo-editor.css";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export function CircularAvatarEditorDialog({ open, onCancel, onSet }: {
  open: boolean;
  onCancel: () => void;
  onSet: (blob: Blob) => Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [setting, setSetting] = useState(false);

  function clearSelection() {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImageUrl(null); setCrop({ x: 0, y: 0 }); setZoom(1); setCropPixels(null); setError(null);
  }

  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape" && !setting) { clearSelection(); onCancel(); } };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, setting]);

  useEffect(() => () => { if (imageUrl) URL.revokeObjectURL(imageUrl); }, [imageUrl]);
  if (!open) return null;

  function selectFile(file: File | undefined) {
    if (!file) return;
    if (!ALLOWED_TYPES.has(file.type)) { setError("JPEG、PNG、WebP形式の画像を選択してください。"); return; }
    if (file.size > MAX_BYTES) { setError("画像サイズが大きすぎます。10MB以下の画像を選択してください。"); return; }
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImageUrl(URL.createObjectURL(file)); setCrop({ x: 0, y: 0 }); setZoom(1); setCropPixels(null); setError(null);
  }

  async function submit() {
    if (!imageUrl || !cropPixels) return;
    setSetting(true); setError(null);
    try { await onSet(await createCroppedAvatarBlob(imageUrl, cropPixels)); clearSelection(); onCancel(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "プロフィール写真を設定できませんでした。"); }
    finally { setSetting(false); }
  }

  return <div className="profile-photo-editor-backdrop" onMouseDown={() => !setting && (clearSelection(), onCancel())}>
    <div ref={dialogRef} className="profile-photo-editor" role="dialog" aria-modal="true" aria-labelledby="profile-photo-editor-title" tabIndex={-1} onMouseDown={(event) => event.stopPropagation()}>
      <h2 id="profile-photo-editor-title" className="profile-photo-editor__header">プロフィール写真</h2>
      <input ref={inputRef} className="profile-photo-editor__file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => selectFile(event.target.files?.[0])} />
      {!imageUrl ? <button className="profile-photo-editor__select" type="button" onClick={() => inputRef.current?.click()}>写真を選択</button> : <>
        <div className="profile-photo-editor__viewport">
          <Cropper image={imageUrl} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_area, pixels) => setCropPixels(pixels)} />
        </div>
        <button className="profile-photo-editor__change" type="button" disabled={setting} onClick={() => inputRef.current?.click()}>写真を選び直す</button>
        <label className="profile-photo-editor__zoom">拡大・縮小<input type="range" min="1" max="3" step="0.01" value={zoom} disabled={setting} onChange={(event) => setZoom(Number(event.target.value))} /></label>
      </>}
      {error ? <p className="profile-photo-editor__error" role="alert">{error}</p> : null}
      <footer className="profile-photo-editor__footer"><button type="button" disabled={setting} onClick={() => { clearSelection(); onCancel(); }}>キャンセル</button><button type="button" disabled={!imageUrl || !cropPixels || setting} onClick={() => void submit()}>{setting ? "設定中..." : "設定"}</button></footer>
    </div>
  </div>;
}
